import React from "react";
import { Text, Tabs, Tab, Flex } from "@jsq/ds/core";
import { SPACING } from "~/styles/variables";
import { getFieldValuesForSection } from "../../utils/dataTransformers";
import { SectionView } from "./SectionView";
import * as styles from "./styles_vanilla.css";
import type { TabWithSections } from "../../../CustomFields/types";
import type { EntityExtensionFieldValue } from "../../types";

export type TabsViewProps = {
  tabsWithSections: TabWithSections[];
  activeTab: string;
  fieldValuesMap: Map<string, EntityExtensionFieldValue>;
  onTabChange: (tabId: string) => void;
  onFieldChange: (fieldId: string, value: string) => void;
};

export const TabsView: React.FC<TabsViewProps> = ({
  tabsWithSections,
  activeTab,
  fieldValuesMap,
  onTabChange,
  onFieldChange,
}) => {
  const activeTabData = tabsWithSections.find(
    (tab) => tab.tabName.toLowerCase().replace(/\s+/g, "-") === activeTab,
  );

  if (!activeTabData) return null;

  const sectionsInTab = activeTabData.sections
    .map((sectionWithFields) => ({
      section: sectionWithFields.section,
      fieldValues: getFieldValuesForSection(sectionWithFields.fields, fieldValuesMap),
    }))
    .filter((s) => s.fieldValues.length > 0);

  return (
    <>
      <div className={styles.tabsContainer}>
        <Tabs hasPaddingInline={false}>
          {tabsWithSections.map((tab) => {
            const tabId = tab.tabName.toLowerCase().replace(/\s+/g, "-");
            const tabSections = tab.sections.map((s) => ({
              section: s.section,
              fieldValues: getFieldValuesForSection(s.fields, fieldValuesMap),
            }));
            const tabFieldCount = tabSections.reduce((sum, s) => sum + s.fieldValues.length, 0);

            return (
              <Tab
                key={tabId}
                text={tab.tabName}
                active={activeTab === tabId}
                counterBadgeProps={{
                  count: tabFieldCount,
                  variant: "important",
                }}
                onClick={() => onTabChange(tabId)}
                data-testid={`section-tab-${tabId}`}
              />
            );
          })}
        </Tabs>
      </div>

      <Flex flexDirection="column" gap={SPACING.MEDIUM} className={styles.activeTabContent}>
        <Text variant="regular-sm" className={styles.tabDescription}>
          {sectionsInTab.length} section
          {sectionsInTab.length === 1 ? "" : "s"} in this tab
        </Text>

        <Flex flexDirection="column" gap={SPACING.MEDIUM}>
          {sectionsInTab.map(({ section, fieldValues }) => (
            <SectionView
              key={section.id}
              section={section}
              fields={fieldValues}
              onFieldChange={onFieldChange}
            />
          ))}
        </Flex>
      </Flex>
    </>
  );
};
